import React, { useState, useEffect, useMemo } from 'react';
import { Container, Grid, Typography, Fab, Box } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import UserFilters from './UserFilters';
import UserTable from './UserTable';
import UserModal from './UserModal';
import DeleteModal from './DeleteModal';

function UsersComponent({ classes, users = [], actions, objects, usersState }) {
  const { t } = useTranslation();
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [wasCreating, setWasCreating] = useState(false);
  const [wasUpdating, setWasUpdating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    paternalSurname: '',
    maternalSurname: '',
    email: '',
    phone: '',
    role: '',
    position: '',
    password: '',
    fullName: '',
  });

  const filteredUsers = useMemo(() => {
    if (!isSearching && !searchName && !searchEmail && !selectedRole) {
      return users;
    }

    return users.filter((user) => {
      const nameMatch =
        !searchName ||
        (user.fullName && user.fullName.toLowerCase().includes(searchName.toLowerCase())) ||
        (user.full_name && user.full_name.toLowerCase().includes(searchName.toLowerCase())) ||
        (user.firstName && user.firstName.toLowerCase().includes(searchName.toLowerCase())) ||
        (user.lastName && user.lastName.toLowerCase().includes(searchName.toLowerCase())) ||
        (user.name && user.name.toLowerCase().includes(searchName.toLowerCase()));

      const emailMatch =
        !searchEmail ||
        (user.email && user.email.toLowerCase().includes(searchEmail.toLowerCase()));

      const roleMatch =
        !selectedRole ||
        (user.role && user.role.name === selectedRole) ||
        (user.role && user.role === selectedRole);

      return nameMatch && emailMatch && roleMatch;
    });
  }, [users, searchName, searchEmail, selectedRole, isSearching]);

  useEffect(() => {
    handleCloseCreateModal();
  }, [objects.users]);

  useEffect(() => {
    let isMounted = true;

    const fetchRoles = async () => {
      if (!isMounted) return;
      setLoadingRoles(true);
      setRolesError(null);

      try {
        const response = await fetch('/api/v1/users/roles');
        const data = await response.json();

        if (!isMounted) return;

        if (response.ok) {
          setRoles(data.roles);
        } else {
          const errorMsg = t('Role load error', { error: data.error });
          setRolesError(errorMsg);
          setRoles([]);
        }
      } catch (error) {
        if (isMounted) {
          const errorMsg = t('Server connection error');
          setRolesError(errorMsg);
          setRoles([]);
        }
      } finally {
        if (isMounted) {
          setLoadingRoles(false);
        }
      }
    };

    fetchRoles();

    return () => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    if (usersState?.createUserLoading && !wasCreating) {
      setWasCreating(true);
    }

    if (wasCreating && !usersState?.createUserLoading && !usersState?.createUserError) {
      handleCloseCreateModal();
      setWasCreating(false);
    }

    if (wasCreating && !usersState?.createUserLoading && usersState?.createUserError) {
      setWasCreating(false);
    }

    if (usersState?.updateUserLoading && !wasUpdating) {
      setWasUpdating(true);
    }

    if (wasUpdating && !usersState?.updateUserLoading && !usersState?.updateUserError) {
      handleCloseCreateModal();
      setWasUpdating(false);
    }

    if (wasUpdating && !usersState?.updateUserLoading && usersState?.updateUserError) {
      setWasUpdating(false);
    }
  }, [
    usersState?.createUserLoading,
    usersState?.createUserError,
    usersState?.updateUserLoading,
    usersState?.updateUserError,
    wasCreating,
    wasUpdating,
  ]);

  useEffect(() => {
    setPage(0);
  }, [searchName, searchEmail, selectedRole]);

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setIsEditMode(false);
    setEditingUser(null);
    setWasCreating(false);
    setWasUpdating(false);
    setShowDeleteModal(false);
  };

  const getRoleId = (roleName) => {
    const role = roles.find((r) => r.name === roleName || r.value === roleName);
    return role ? role.id : null;
  };

  const getRoleChipColor = (roleName) => {
    switch (roleName) {
      case 'admin':
        return 'primary';
      case 'operator':
        return 'secondary';
      case 'packing_manager':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (roleName) => {
    const role = roles.find((r) => r.value === roleName);
    return role ? role.label : roleName;
  };

  const handleClearSearch = () => {
    setSearchName('');
    setSearchEmail('');
    setSelectedRole('');
    setIsSearching(false);
    setPage(0);
  };

  const handleSearch = () => {
    setIsSearching(true);
    setPage(0);
  };

  const handleSearchNameChange = (e) => {
    setSearchName(e.target.value);
  };

  const handleSearchEmailChange = (e) => {
    setSearchEmail(e.target.value.toLowerCase());
  };

  const handleSelectedRoleChange = (e) => {
    setSelectedRole(e.target.value);
  };

  const handleMenuClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleEdit = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setIsEditMode(true);
      setEditingUser(user);
      setFormData({
        fullName: user.fullName || user.full_name || '',
        email: (user.email || '').toLowerCase(),
        phone: user.phone || '',
        role: user.role?.name || '',
        position: user.position || '',
        firstName: '',
        lastName: '',
        paternalSurname: '',
        maternalSurname: '',
        password: '',
      });

      setShowCreateModal(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setUserToDelete(user);
      setShowDeleteModal(true);
    }
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddUser = () => {
    if (roles.length === 0) {
      console.warn(t('Cannot create user roles unavailable'));
      return;
    }

    setIsEditMode(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      paternalSurname: '',
      maternalSurname: '',
      email: '',
      phone: '',
      role: '',
      position: '',
      password: '',
      fullName: '',
    });
    setShowCreateModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (isEditMode) {
      const { fullName, email, phone, role, position } = formData;
      const roleId = getRoleId(role);
      if (!roleId) {
        console.error(t('Role not valid'), role);
        return;
      }

      if (!fullName || !fullName.trim()) {
        console.error(t('Full name required'));
        return;
      }

      if (!email || !phone || !position) {
        console.error(t('All fields required'));
        return;
      }

      const updateUserPayload = {
        name: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        position: position.trim(),
        roleId,
      };
      actions.updateUser(editingUser.id, updateUserPayload);
    } else {
      const { firstName, lastName, paternalSurname, maternalSurname, ...restData } = formData;
      const fullName = [firstName, lastName, paternalSurname, maternalSurname]
        .filter((part) => part && part.trim() !== '')
        .join(' ');

      const roleId = getRoleId(formData.role);

      if (!roleId) {
        console.error(t('Role not valid'), formData.role);
        return;
      }

      if (!fullName.trim()) {
        console.error(t('Name required'));
        return;
      }

      if (!restData.email || !restData.password) {
        console.error(t('Email and password required'));
        return;
      }

      const createUserPayload = {
        name: fullName,
        email: restData.email.trim().toLowerCase(),
        phone: restData.phone,
        position: restData.position,
        password: restData.password,
        roleId,
      };
      actions.createUser(createUserPayload);
    }
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      actions.deleteUser(userToDelete.id);
    }
    handleCloseDeleteModal();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3}>
        {/* Título */}
        <Grid item xs={12}>
          <Typography variant="h3" component="h2" className={classes.marginTop}>
            {t('User management')}
          </Typography>
        </Grid>

        {/* Mostrar error de roles si existe */}
        {rolesError && (
          <Grid item xs={12}>
            <Box
              p={2}
              style={{
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
              }}>
              <Typography variant="body2" style={{ color: '#c62828' }}>
                {t('Roles error', { error: rolesError })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Filtros */}
        <Grid item xs={12}>
          <UserFilters
            searchName={searchName}
            searchEmail={searchEmail}
            selectedRole={selectedRole}
            roles={roles}
            onSearchNameChange={handleSearchNameChange}
            onSearchEmailChange={handleSearchEmailChange}
            onSelectedRoleChange={handleSelectedRoleChange}
            onClearSearch={handleClearSearch}
            onSearch={handleSearch}
          />
        </Grid>

        {/* Mostrar información de resultados */}
        {(isSearching || searchName || searchEmail || selectedRole) && (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {filteredUsers.length === 0
                ? t('No users found with applied filters')
                : t('Showing users count', { count: filteredUsers.length, total: users.length })}
            </Typography>
          </Grid>
        )}

        {/* Tabla */}
        <Grid item xs={12}>
          <UserTable
            users={filteredUsers}
            page={page}
            rowsPerPage={rowsPerPage}
            anchorEl={anchorEl}
            selectedUserId={selectedUserId}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onMenuClick={handleMenuClick}
            onMenuClose={handleMenuClose}
            onEdit={handleEdit}
            onDelete={handleDelete}
            getRoleChipColor={getRoleChipColor}
            getRoleLabel={getRoleLabel}
          />
        </Grid>
      </Grid>

      {/* Botón flotante */}
      <Fab
        color="primary"
        aria-label="add"
        className={classes.fab}
        onClick={handleAddUser}
        disabled={loadingRoles || roles.length === 0}
        style={{
          opacity: loadingRoles || roles.length === 0 ? 0.5 : 1,
        }}>
        <AddIcon />
      </Fab>

      {/* Modales */}
      <UserModal
        open={showCreateModal}
        isEditMode={isEditMode}
        formData={formData}
        roles={roles}
        onClose={handleCloseCreateModal}
        onSubmit={handleFormSubmit}
        onInputChange={handleInputChange}
        loading={usersState?.createUserLoading || usersState?.updateUserLoading || false}
        error={usersState?.createUserError || usersState?.updateUserError || null}
      />

      <DeleteModal
        open={showDeleteModal}
        user={userToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
}

export default UsersComponent;
